import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TicketGrid from "@/components/TicketGrid";
import { toast } from "sonner";
import { Calendar, Trophy, Smartphone, Upload, Loader2, ShoppingCart, X, CheckCircle2 } from "lucide-react";

const FALLBACK_IMAGE =
    "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=1200&q=80&auto=format&fit=crop";
const YAPE_FALLBACK_QR =
    "https://images.pexels.com/photos/278430/pexels-photo-278430.jpeg?w=600&auto=compress";

function backendImg(url) {
    if (!url) return null;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
}

export default function RaffleDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [raffle, setRaffle] = useState(null);
    const [selected, setSelected] = useState([]);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [receipt, setReceipt] = useState(null);
    const [receiptUrl, setReceiptUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [opCode, setOpCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [buyer, setBuyer] = useState({ full_name: "", email: "", phone: "", dni: "", shipping_address: "" });
    const buyerForm = useRef(null);
    const fileRef = useRef(null);

    const load = () => {
        api.get(`/raffles/${id}`).then((r) => setRaffle(r.data));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (!raffle) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin" />
            </div>
        );
    }

    const toggle = (n) => {
        setSelected((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
    };

    const total = (selected.length * raffle.price_per_ticket).toFixed(2);

    const handleReceiptUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setReceipt(file);
        setReceiptUrl(null);
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(user ? "/upload" : "/guest/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setReceiptUrl(data.url);
            toast.success("Comprobante subido correctamente");
        } catch (err) {
            toast.error(formatApiError(err));
            setReceipt(null);
        } finally {
            setUploading(false);
        }
    };

    const startCheckout = () => {
        if (selected.length === 0) {
            toast.error("Selecciona al menos un boleto");
            return;
        }
        setBuyer((prev) => ({ ...prev, full_name: prev.full_name || user?.name || "", email: prev.email || user?.email || "" }));
        setCheckoutOpen(true);
    };

    const submitPurchase = async () => {
        if (!buyerForm.current?.reportValidity()) return;
        if (!receiptUrl || uploading || submitting) {
            toast.error("Sube tu comprobante de Yape primero");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/raffles/${id}/purchase`, {
                ...buyer,
                ticket_numbers: selected,
                receipt_url: receiptUrl,
                operation_code: opCode,
            });
            toast.success("¡Compra registrada! Esperando validación del admin.");
            setCheckoutOpen(false);
            setSelected([]);
            setReceipt(null);
            setReceiptUrl(null);
            setOpCode("");
            load();
            navigate(user ? "/dashboard" : `/compra/${data.id}#${data.access_token}`);
        } catch (err) {
            toast.error(formatApiError(err));
            if (err.response?.status === 409) load();
        } finally {
            setSubmitting(false);
        }
    };

    const heroImg = backendImg(raffle.image_url) || FALLBACK_IMAGE;
    const yapeQR = backendImg(raffle.yape_qr_url) || YAPE_FALLBACK_QR;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="raffle-detail-page">
            <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5">
                    <div className="brut-card overflow-hidden mb-6">
                        <img src={heroImg} alt={raffle.title} className="w-full aspect-square object-cover border-b-2 border-ink" />
                        <div className="p-5 space-y-3">
                            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase leading-tight">{raffle.title}</h1>
                            <div className="flex items-center gap-2 text-base font-bold">
                                <Trophy className="w-5 h-5 text-coral" strokeWidth={3} /> {raffle.prize}
                            </div>
                            <p className="text-ink/70 whitespace-pre-line">{raffle.description}</p>
                            <div className="grid grid-cols-2 gap-3 pt-3 border-t-2 border-dashed border-ink">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Precio boleto</div>
                                    <div className="font-display font-black text-2xl">S/ {raffle.price_per_ticket.toFixed(2)}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Fecha sorteo</div>
                                    <div className="font-bold flex items-center gap-1">
                                        <Calendar className="w-4 h-4" strokeWidth={3} />
                                        {new Date(raffle.draw_date).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" })}
                                    </div>
                                </div>
                            </div>
                            <div className="text-xs uppercase tracking-widest font-bold opacity-60 pt-2">Organiza: {raffle.creator_name || "—"}</div>
                            {raffle.status === "drawn" && raffle.winner_number && (
                                <div className="border-2 border-ink bg-coral text-white p-4 shadow-brut">
                                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">¡Sorteo realizado!</div>
                                    <div className="font-display font-black text-3xl">Ganador: #{raffle.winner_number}</div>
                                    <div className="text-sm">{raffle.winner_name}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <div className="brut-card p-6 mb-6 sticky top-20 z-20">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-xs uppercase tracking-widest font-bold opacity-60">Tu carrito</div>
                                <div className="font-display font-black text-3xl">
                                    {selected.length} {selected.length === 1 ? "boleto" : "boletos"} · S/ {total}
                                </div>
                            </div>
                            <button
                                onClick={startCheckout}
                                disabled={selected.length === 0 || raffle.status !== "approved"}
                                className="brut-btn brut-btn-primary"
                                data-testid="checkout-btn"
                            >
                                <ShoppingCart className="w-4 h-4" strokeWidth={3} /> Comprar
                            </button>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            <div className="border-2 border-ink p-2 text-center">
                                <span className="inline-block w-3 h-3 bg-white border-2 border-ink mr-1 align-middle" /> Disponible
                            </div>
                            <div className="border-2 border-ink p-2 text-center">
                                <span className="inline-block w-3 h-3 bg-mint border-2 border-ink mr-1 align-middle" /> Seleccionado
                            </div>
                            <div className="border-2 border-ink p-2 text-center">
                                <span className="inline-block w-3 h-3 bg-gray-200 border-2 border-gray-400 mr-1 align-middle" /> Vendido
                            </div>
                        </div>
                    </div>

                    <div className="brut-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-xs uppercase tracking-widest font-bold opacity-60">Elige tus números</div>
                                <div className="font-display font-black text-2xl">{raffle.tickets_sold}/{raffle.total_tickets} vendidos</div>
                            </div>
                        </div>
                        <TicketGrid
                            total={raffle.total_tickets}
                            selected={selected}
                            soldNumbers={raffle.sold_numbers}
                            onToggle={toggle}
                            winner={raffle.winner_number}
                        />
                    </div>
                </div>
            </div>

            {/* Checkout Modal */}
            {checkoutOpen && (
                <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4" data-testid="checkout-modal">
                    <div className="brut-card bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b-2 border-ink">
                            <div>
                                <div className="text-xs uppercase tracking-widest font-bold opacity-60">Paga con Yape</div>
                                <div className="font-display font-black text-3xl">Total: S/ {total}</div>
                            </div>
                            <button disabled={submitting || uploading} onClick={() => setCheckoutOpen(false)} className="brut-btn p-2" data-testid="close-checkout">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <form ref={buyerForm} onSubmit={(e) => e.preventDefault()} className="p-5 border-b-2 border-ink grid sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <h2 className="font-display font-black text-xl">Datos del comprador</h2>
                                <p className="text-sm text-ink/70">Compra sin registrarte. Usaremos estos datos para contactarte y enviarte el premio si ganas.</p>
                            </div>
                            {[
                                { key: "full_name", label: "Nombres y apellidos", autoComplete: "name", minLength: 2, maxLength: 120 },
                                { key: "email", label: "Correo electrónico", type: "email", autoComplete: "email", maxLength: 254 },
                                { key: "phone", label: "Teléfono", type: "tel", autoComplete: "tel", pattern: "[+]?[0-9 ]{7,20}", placeholder: "+51 999 999 999", maxLength: 20 },
                                { key: "dni", label: "DNI", inputMode: "numeric", pattern: "[0-9]{8}", maxLength: 8, title: "Ingresa los 8 dígitos de tu DNI" },
                            ].map(({ key, label, ...props }) => (
                                <div key={key}>
                                    <label className="brut-label" htmlFor={key}>{label} *</label>
                                    <input {...props} id={key} required className="brut-input" value={buyer[key]} onChange={(e) => setBuyer({ ...buyer, [key]: e.target.value })} />
                                </div>
                            ))}
                            <div className="sm:col-span-2">
                                <label className="brut-label" htmlFor="shipping_address">Dirección de envío del premio *</label>
                                <textarea id="shipping_address" required minLength={10} maxLength={500} autoComplete="street-address" className="brut-input" rows={3}
                                    placeholder="Calle y número, distrito, provincia, departamento y referencia"
                                    value={buyer.shipping_address} onChange={(e) => setBuyer({ ...buyer, shipping_address: e.target.value })} />
                            </div>
                        </form>
                        <div className="p-5 grid md:grid-cols-2 gap-5">
                            <div className="space-y-3">
                                <div className="border-2 border-yape bg-yape text-white p-3 shadow-brut text-center">
                                    <div className="flex items-center justify-center gap-2 mb-1">
                                        <Smartphone className="w-5 h-5" strokeWidth={3} />
                                        <span className="font-display font-black text-xl uppercase">Yape</span>
                                    </div>
                                    {raffle.yape_phone && (
                                        <div className="text-sm">Número: <span className="font-bold">{raffle.yape_phone}</span></div>
                                    )}
                                </div>
                                <div className="brut-card overflow-hidden">
                                    <img src={yapeQR} alt="Yape QR" className="w-full aspect-square object-cover" />
                                </div>
                                <p className="text-xs text-ink/70">
                                    1) Abre Yape · 2) Escanea el QR o yapea al número · 3) Sube tu captura del comprobante.
                                </p>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="brut-label">Boletos seleccionados</label>
                                    <div className="flex flex-wrap gap-1">
                                        {[...selected].sort((a, b) => a - b).map((n) => (
                                            <span key={n} className="border-2 border-ink bg-mint px-2 py-1 text-xs font-bold">
                                                #{n}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="brut-label" htmlFor="op-code">Código de operación (opcional)</label>
                                    <input
                                        id="op-code"
                                        type="text"
                                        value={opCode}
                                        onChange={(e) => setOpCode(e.target.value)}
                                        placeholder="Ej. 123456789"
                                        className="brut-input"
                                        data-testid="op-code-input"
                                    />
                                </div>
                                <div>
                                    <label className="brut-label">Captura del comprobante Yape *</label>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleReceiptUpload}
                                        className="hidden"
                                        data-testid="receipt-file-input"
                                    />
                                    <button
                                        type="button"
                                        disabled={uploading || submitting}
                                        onClick={() => fileRef.current?.click()}
                                        className="brut-btn w-full"
                                        data-testid="receipt-upload-btn"
                                    >
                                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={3} />}
                                        {receipt ? "Cambiar comprobante" : "Subir comprobante"}
                                    </button>
                                    {receiptUrl && (
                                        <div className="mt-2 flex items-center gap-2 text-xs text-green-700 font-bold">
                                            <CheckCircle2 className="w-4 h-4" /> Comprobante subido
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={submitPurchase}
                                    disabled={!receiptUrl || uploading || submitting}
                                    className="brut-btn brut-btn-yape w-full"
                                    data-testid="submit-purchase-btn"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar compra"}
                                </button>
                                <p className="text-xs text-ink/60">
                                    El administrador validará tu pago. Al terminar podrás consultar tu compra y crear una cuenta opcionalmente.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!user && (
                <div className="mt-10 brut-card p-6 text-center">
                    <p className="font-bold">Compra sin crear una cuenta. Si ya tienes una, puedes <Link to="/login" className="underline text-coral">iniciar sesión</Link> para guardar tus compras en tu panel.</p>
                </div>
            )}
        </div>
    );
}
